import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';

// SVG mask 定義：在頂部中間創建洞（膠囊形 + 圓形）
// 在 mask 中，白色=顯示，黑色=隱藏（洞），所以需要反轉顏色
const createMaskSVG = (cardWidth) => {
    // 使用絕對像素值，不根據長寬比縮放
    const capsuleHoleWidth = 150; // 洞的寬度（像素）
    const capsuleHoleHeight = 30; // 洞的高度（像素）
    const circleRadius = 20; // 圓形半徑（像素）= 40
    
    // viewBox 寬度設為卡片寬度，這樣 1 viewBox 單位 = 1px，洞的大小就是絕對的
    const viewBoxWidth = cardWidth || 500; // 如果沒有傳入寬度，使用默認值
    const viewBoxHeight = 2000; // 足夠大的高度
    
    // 計算洞在 viewBox 中的位置（水平居中）
    const holeX = (viewBoxWidth - capsuleHoleWidth) / 2;
    const holeCenterX = viewBoxWidth / 2;
    const capsuleY = 40; // 膠囊的 y 位置
    const circleY = capsuleY; // 圓形的中心 y 位置，與膠囊上緣對齊
    
    const svg = `
        <svg width="100%" height="100%" viewBox="0 0 ${viewBoxWidth} ${viewBoxHeight}" preserveAspectRatio="xMidYMin meet" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <mask id="cardMask">
                    <rect width="100%" height="100%" fill="white" />
                    <g fill="black">
                        <!-- 底下的長膠囊 - 水平居中 -->
                        <!-- rx 和 ry 都設為高度的一半，形成左右圓角的膠囊形狀 -->
                        <rect x="${holeX}" y="${capsuleY}" width="${capsuleHoleWidth}" height="${capsuleHoleHeight}" rx="${capsuleHoleHeight / 2}" ry="${capsuleHoleHeight / 2}" />
                        <!-- 上面的凸半圓，中心剛好在膠囊上緣 - 水平居中 -->
                        <circle cx="${holeCenterX}" cy="${circleY}" r="${circleRadius}" />
                    </g>
                </mask>
            </defs>
            <rect width="100%" height="100%" fill="white" mask="url(#cardMask)" />
        </svg>
    `.trim().replace(/\s+/g, ' '); // 移除多餘空白，但保持可讀性
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const CardWrapper = styled.div.attrs(props => ({
    style: {
        '--mask-svg': props.maskSvg || createMaskSVG(props.width || 500)
    }
}))`
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: ${props => props.title ? '100px 30px 30px 30px' : '30px'};
    margin: 0 auto ${props => props.marginBottom || 30}px auto;
    width: ${props => props.width || 500}px;
    ${props => props.height ? `height: ${props.height};` : ''}
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s ease;
    position: relative;

    /* 使用偽元素在頂部創建遮罩層來挖洞 */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        border-radius: 20px;
        z-index: -1;
    }

    /* 使用 SVG mask 在頂部中間創建洞（膠囊形 + 圓形） */
    mask-image: var(--mask-svg);
    -webkit-mask-image: var(--mask-svg);
    mask-size: 100% auto;
    -webkit-mask-size: 100% auto;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
    mask-position: top center;
    -webkit-mask-position: top center;

    &:hover {
        transform: translateY(-5px);
    }

    @media (max-width: 768px) {
        padding: ${props => props.title ? '85px 20px 20px 20px' : '20px'};
        width: 100%;
        max-width: ${props => props.maxWidth || 314}px;
        margin-left: auto;
        margin-right: auto;
    }

    @media (max-width: 375px) {
        padding: ${props => props.title ? '80px 15px 15px 15px' : '15px'};
        width: 100%;
        max-width: ${props => props.maxWidth ? Math.min(props.maxWidth, 280) : 280}px;
        margin-left: auto;
        margin-right: auto;
    }
`;

const CardTitle = styled.h2`
    font-size: 2rem;
    margin-bottom: 20px;
    margin-top: 0;
    text-align: center;
    font-weight: 600;

    @media (max-width: 768px) {
        font-size: 1.5rem;
        margin-bottom: 15px;
    }

    @media (max-width: 375px) {
        font-size: 1.25rem;
        margin-bottom: 12px;
    }
`;

const ProductCard = ({ 
    children, 
    title, 
    width, 
    height,
    maxWidth, 
    marginBottom,
    className,
    ...props 
}) => {
    const cardRef = useRef(null);
    const [actualWidth, setActualWidth] = useState(width || 500);
    useEffect(() => {
        const updateDimensions = () => {
            if (cardRef.current) {
                const rect = cardRef.current.getBoundingClientRect();
                setActualWidth(rect.width);
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [width, maxWidth]);

    return (
        <CardWrapper 
            ref={cardRef}
            width={width}
            height={height}
            maxWidth={maxWidth}
            marginBottom={marginBottom}
            title={title}
            className={className}
            maskSvg={createMaskSVG(actualWidth)}
            {...props}
        >
            {title && <CardTitle>{title}</CardTitle>}
            {children}
        </CardWrapper>
    );
};

export default ProductCard;

